import { useEffect, useMemo, useRef, useState, Children, isValidElement, Fragment } from "react";
import axios from "axios";
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set when a drag finishes so the overlay's forwarded click is suppressed
// (a drop can otherwise fire a click and open the card underneath).
let lastDragEndAt = 0;

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.9 : 1,
    // iOS: suppress the long-press link preview / text-selection magnifier
    // that hijacks touch drags on anchor cards before dnd-kit can activate.
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    userSelect: "none",
    touchAction: "manipulation",
  };
  // Quick taps land on the overlay (never on the card's <a>/<button>, so iOS
  // has no link gesture to hijack) and are forwarded to the card's root.
  const forwardTap = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (Date.now() - lastDragEndAt < 300) return;
    const card = e.currentTarget.parentElement?.querySelector("a,button");
    if (card) card.click();
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      data-sortable-card="1"
      className={`relative h-full [&>*]:w-full [&>*]:h-full ${isDragging ? "ring-2 ring-blue-400 rounded-xl shadow-lg" : ""}`}
    >
      {children}
      <div aria-hidden="true" data-drag-overlay="1" className="absolute inset-0 z-[1]" onClick={forwardTap} />
    </div>
  );
}

/**
 * Wraps a grid of menu cards with long-press drag-to-reorder (dnd-kit).
 * Order persists per login via GET/PUT /api/card-order. Cards not in the
 * saved order fall to the end in default order; stale ids are ignored.
 */
export default function ReorderableGrid({ canReorder = false, gridClassName = "", menuGridId, children }) {
  const items = useMemo(() => {
    const arr = [];
    const collect = (nodes) => {
      Children.forEach(nodes, (child) => {
        if (!isValidElement(child)) return;
        if (child.type === Fragment) { collect(child.props.children); return; }
        const id = child.props?.testId || child.props?.["data-testid"] || child.key || `card-${arr.length}`;
        arr.push({ id: String(id), el: child });
      });
    };
    collect(children);
    return arr;
  }, [children]);

  const [order, setOrder] = useState(null);
  const [resetArmed, setResetArmed] = useState(false);
  const loaded = useRef(false);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  useEffect(() => {
    if (!canReorder || loaded.current) return;
    loaded.current = true;
    axios.get(`${API}/card-order`, { headers: authHeaders() })
      .then(res => setOrder(res.data?.order || null))
      .catch(() => {});
  }, [canReorder]);

  const sorted = useMemo(() => {
    if (!order) return items;
    const pos = new Map(order.map((id, i) => [id, i]));
    const inOrder = items.filter(it => pos.has(it.id)).sort((a, b) => pos.get(a.id) - pos.get(b.id));
    const rest = items.filter(it => !pos.has(it.id));
    return [...inOrder, ...rest];
  }, [items, order]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 350, tolerance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 350, tolerance: 8 } })
  );

  const onDragEnd = ({ active, over }) => {
    lastDragEndAt = Date.now();
    if (!over || active.id === over.id) return;
    const ids = sorted.map(it => it.id);
    const newIds = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    setOrder(newIds);
    axios.put(`${API}/card-order`, { order: newIds }, { headers: authHeaders() })
      .catch(e => console.error("Failed to save card order:", e));
  };

  const resetLayout = () => {
    setResetArmed(false);
    setOrder(null);
    axios.delete(`${API}/card-order`, { headers: authHeaders() })
      .catch(e => console.error("Failed to reset card order:", e));
  };

  if (!canReorder) {
    return <div data-menu-grid={menuGridId} className={gridClassName}>{items.map(it => <div key={it.id} data-sortable-card="1" className="h-full [&>*]:w-full [&>*]:h-full">{it.el}</div>)}</div>;
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} autoScroll>
        <SortableContext items={sorted.map(it => it.id)} strategy={rectSortingStrategy}>
          <div data-menu-grid={menuGridId} className={gridClassName} data-testid="reorderable-grid">
            {sorted.map(it => (
              <SortableItem key={it.id} id={it.id}>{it.el}</SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {order && (
        <div className="flex justify-end mt-1">
          {resetArmed ? (
            <span className="flex items-center gap-2">
              <button type="button" onClick={resetLayout} data-testid="reset-card-layout-confirm"
                className="text-[10px] font-bold text-red-600 underline">
                Tap to confirm reset
              </button>
              <button type="button" onClick={() => setResetArmed(false)} data-testid="reset-card-layout-cancel"
                className="text-[10px] text-gray-400 underline">
                cancel
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setResetArmed(true)} data-testid="reset-card-layout-btn"
              className="text-[10px] text-gray-400 hover:text-gray-600 underline">
              Reset card layout
            </button>
          )}
        </div>
      )}
    </>
  );
}
